// Brain/voice data layer for the GASS character-config panel (ENG-833).
//
// Bridges a GASS character (AAM side, keyed by slug === character.id) to its
// Convorcher "brain" (persona + voice + vocal cues + gestures) over the
// /admin/* channel (convorcherAdmin). AAM and Convorcher are linked ONLY by the
// shared name string — there is no foreign key — so resolution is a name match,
// and a name can carry several versions. The live session resolves voice/persona
// from the most recent version (convorcher GetCharacterVoiceID "by most recent
// version"; story import bumps the version), so the panel edits the
// highest-version row. No brain for a slug → the unconfigured state (Q3).

import { convorcherAdmin } from './convorcherAdmin.js'
import { eventGraphState } from './eventGraphState.js'
import { findConvorcherSessionNode } from './sessionCharacter.js'

const norm = (s) => String(s ?? '').trim().toLowerCase()

// sessionCharacterOverride — the character a live Convorcher session actually
// opens with, if the active character's Event Graph declares one via a
// `convorcherSession` node. A splat/rendered character (slug e.g. "isaac-ogs")
// commonly converses "as" a different brain (character "isaac") — see
// ConvorcherClient / the node's "converse as a persona distinct from the
// rendered character". The management panel MUST resolve the same brain the
// session uses, not the raw AAM slug, or it would show "no brain" for a
// character that talks fine. Returns null when there's no override.
// Resolves through the shared finder (ENG-907) so the panel and the live
// session can never disagree about which node counts — including a node nested
// one level inside a graphGroup, which the old local `find` missed and reported
// as "no brain" for a character that talks fine.
export function sessionCharacterOverride() {
  const node = findConvorcherSessionNode(eventGraphState?.nodes)
  const c = node?.data?.character
  const trimmed = c == null ? '' : String(c).trim()
  return trimmed || null
}

// effectiveBrainName — the name to resolve a brain by for a GASS slug: the
// session override when present (runtime truth), else the slug itself.
export function effectiveBrainName(slug) {
  return sessionCharacterOverride() || slug
}

// resolveBrainCharacter — pick the brain row a GASS slug maps to: name-equal
// (case-insensitive), highest version. Pure; unit-tested. Returns null when
// nothing matches (caller renders "no brain configured").
export function resolveBrainCharacter(slug, brains) {
  if (!slug || !Array.isArray(brains)) return null
  const matches = brains.filter((b) => norm(b.name) === norm(slug))
  if (matches.length === 0) return null
  return matches.reduce((best, b) => ((b.version ?? 0) > (best.version ?? 0) ? b : best))
}

// characterSelectOptions (ENG-875) — options for the Convorcher Session
// node's CHARACTER dropdown. The WS ?character= param matches a brain row's
// name, and one name spans several versions (the server resolves the most
// recent), so the list is unique lowercased names, sorted. A current value
// missing from the list is preserved as a flagged option, never dropped.
export function characterSelectOptions(brains, current) {
  const names = [...new Set((brains ?? []).map((b) => norm(b.name)).filter(Boolean))].sort()
  const opts = names.map((n) => ({ value: n, label: n, missing: false }))
  const cur = current ?? ''
  if (cur && !names.includes(norm(cur))) {
    opts.push({ value: cur, label: `${cur} (not in list)`, missing: true })
  }
  return opts
}

// gestureNames — normalize an anim-config gestures payload (array of
// {name,...} objects) to a plain string list for the editor.
export function gestureNames(animConfig) {
  return (animConfig?.gestures ?? []).map((g) => (typeof g === 'string' ? g : g?.name)).filter(Boolean)
}

// unwrap — the /admin list route returns a bare array; the MCP wrapper returns
// { ok, result }. Accept either so the module is robust to both callers.
function unwrap(res) {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.result)) return res.result
  return []
}

// loadBrainVoice — resolve the brain for a slug and fetch everything the panel
// shows in one shot. Throws on a server error (loud, per UX rules). Returns
// { brain: null } when the slug has no brain yet.
export async function loadBrainVoice(slug, opts = {}) {
  // Resolve by the character the live session actually uses (convorcherSession
  // override), not the raw AAM slug — so the panel manages the same brain the
  // conversation does.
  const brainName = effectiveBrainName(slug)
  const list = await convorcherAdmin.listBrainCharacters(opts)
  const brain = resolveBrainCharacter(brainName, unwrap(list))
  if (!brain) return { brain: null, slug, brainName }

  const [detail, anim, threshold] = await Promise.all([
    convorcherAdmin.getBrainCharacter(brain.id, opts),
    convorcherAdmin.getAnimConfig(brain.id, opts),
    convorcherAdmin.getSimilarityThreshold(brain.id, opts),
  ])
  return {
    brain,
    slug,
    id: brain.id,
    name: brain.name,
    version: brain.version,
    personality: detail?.personality ?? '',
    voiceId: detail?.voiceId ?? '',
    vocalCues: anim?.vocalCues ?? [],
    gestures: gestureNames(anim),
    stories: detail?.stories ?? [],
    similarityThreshold: threshold?.similarityThreshold ?? null,
    effectiveThreshold: threshold?.effectiveThreshold ?? null,
  }
}

// Per-field save helpers — each hits one admin endpoint. Kept separate so the
// panel can show independent dirty/saving/error state per field (UX proximity
// rule) rather than one all-or-nothing save.
export const saveBrainVoice = {
  personality: (id, personality) => convorcherAdmin.updatePersonality(id, personality),
  voiceId: (id, voiceId) => convorcherAdmin.setVoice(id, voiceId),
  vocalCues: (id, vocalCues) => convorcherAdmin.setVocalCues(id, vocalCues),
  gestures: (id, gestures) => convorcherAdmin.setGestures(id, gestures),
  // number | null (null clears the override). Returns the server's
  // { similarityThreshold, effectiveThreshold } so the editor can refresh both.
  similarityThreshold: (id, value) => convorcherAdmin.setSimilarityThreshold(id, value),
}
